import { createFileRoute } from '@tanstack/react-router'
import { MarketingPage } from '../components/MarketingPage.js'

export const Route = createFileRoute('/refund')({
  component: RefundPage,
})

function RefundPage() {
  return (
    <MarketingPage
      eyebrow="Legal"
      title="Refund and cancellation policy"
      effectiveDate="17 May 2026"
    >
      <p>
        This policy explains how Brightstars handles cancellations and refunds
        for two separate categories of payment:
      </p>
      <ul>
        <li>
          <strong>Subscription fees</strong> paid by academies to Brightstars
          for use of the platform.
        </li>
        <li>
          <strong>Tuition or activity fees</strong> paid by parents to an
          academy through the Brightstars fee collection feature.
        </li>
      </ul>

      <h2>1. Academy subscriptions</h2>

      <h3>1.1 Free trial</h3>
      <p>
        Every paid plan starts with a free trial (typically 30 days). During
        the trial you can cancel at any time and you will not be charged.
        Subscriptions automatically begin at the end of the trial unless
        cancelled.
      </p>

      <h3>1.2 Monthly subscriptions</h3>
      <p>
        You can cancel a monthly subscription at any time from the billing
        screen in your dashboard. Cancellation takes effect at the end of the
        current billing cycle. Your academy retains access until that date,
        after which renewal stops. Monthly fees are non-refundable for the
        current month.
      </p>

      <h3>1.3 Annual subscriptions</h3>
      <p>
        Annual subscriptions can be cancelled within 7 days of the initial
        payment or annual renewal for a full refund, provided the Service
        has not been used in any meaningful way (e.g. no fee invoices raised,
        no communications sent, fewer than 5 students added). Refund requests
        after the 7-day window will be assessed on a pro-rated basis at our
        discretion.
      </p>

      <h3>1.4 Service interruptions</h3>
      <p>
        If we fail to deliver the Service for a continuous period exceeding
        24 hours due to a fault on our side (excluding scheduled maintenance
        announced in advance and outages caused by third-party providers
        outside our control), you may request a pro-rated credit applied to
        your next invoice.
      </p>

      <h3>1.5 How to request a refund</h3>
      <p>
        Email{' '}
        <a href="mailto:billing@brightstars.in">billing@brightstars.in</a>{' '}
        from the email address registered on your academy account, with your
        academy name, the date of payment, and the reason for the request.
        We respond to refund requests within 3 business days. Approved
        refunds are processed back to the original Razorpay payment method
        and typically reflect in your account within 5 to 10 business days.
      </p>

      <h2>2. Parent fee payments</h2>
      <p>
        When parents pay tuition, exam, or activity fees through the
        Brightstars platform, the payment is processed by Razorpay and
        settled directly to the relevant academy. Brightstars is a
        technology facilitator. We do not hold these funds and we do not
        decide whether a refund is due.
      </p>
      <p>
        Refund and cancellation terms for parent fees are set by each
        individual academy. Parents must contact their academy directly to
        request a refund. Once an academy authorises a refund, it is
        processed through Razorpay back to the parent&apos;s original
        payment method, typically within 5 to 10 business days.
      </p>
      <p>
        Failed or duplicate payments where money is debited but the
        transaction does not complete are automatically reversed by Razorpay
        within 7 working days. If this does not happen, contact{' '}
        <a href="mailto:billing@brightstars.in">billing@brightstars.in</a>{' '}
        and we will help reconcile the transaction.
      </p>

      <h2>3. Disputes and chargebacks</h2>
      <p>
        If you believe a charge is incorrect, please contact us at{' '}
        <a href="mailto:billing@brightstars.in">billing@brightstars.in</a>{' '}
        before initiating a chargeback with your bank or card issuer.
        Chargebacks raised without first attempting to resolve the issue
        directly may delay resolution and lead to account suspension while
        the dispute is investigated.
      </p>

      <h2>4. Taxes</h2>
      <p>
        Subscription refunds, where due, are processed inclusive of the GST
        component that was originally charged. The corresponding credit note
        is shared via email and made available for download in your billing
        dashboard.
      </p>

      <h2>5. Contact</h2>
      <p>
        For any refund or billing question, email{' '}
        <a href="mailto:billing@brightstars.in">billing@brightstars.in</a> or
        visit our <a href="/contact">contact page</a>.
      </p>
    </MarketingPage>
  )
}
